import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Spin,
	Space,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { requestGetCustomerById, requestUpdateCustomer } from "../../services/admin.service";
import { PiArrowLeftDuotone, PiEyeDuotone } from "react-icons/pi";

const { Title } = Typography;
const { Option } = Select;

// Constants based on actual user data structure
const ROLE_OPTIONS = [
	{ label: "User", value: "USER" },
	{ label: "Admin", value: "ADMIN" },
];

function EditCustomerPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [customerData, setCustomerData] = useState(null);

	useEffect(() => {
		document.title = "Cloudiva Console | Edit User";
		if (id) {
			fetchCustomerData();
		}
	}, [id]);

	const fetchCustomerData = () => {
		setInitialLoading(true);
		requestGetCustomerById(id)
			.then((response) => {
				const customer = response.data.data || response.data;
				setCustomerData(customer);

				// Populate form with existing data
				form.setFieldsValue({
					name: customer.name,
					email: customer.email,
					phone: customer.phone?.startsWith("+") ? customer.phone : `+${customer.phone}`,
					role: customer.role || "USER",
				});
			})
			.catch(() => {
				notification.error({
					message: "Error",
					description: "Failed to fetch user data",
				});
				navigate("/customers");
			})
			.finally(() => {
				setInitialLoading(false);
			});
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setLoading(true);

				// Process the form data to match the actual user data structure
				const userData = {
					name: values.name,
					email: values.email,
					phone: values.phone?.startsWith("+") ? values.phone : `+${values.phone}`,
					role: values.role || "USER",
				};

				requestUpdateCustomer(id, userData)
					.then(() => {
						message.success("User updated successfully!");
						navigate("/customers");
					})
					.catch((error) => {
						console.error("Error updating user:", error);
						if (error.response?.data?.message) {
							notification.error({
								message: "Error",
								description: error.response.data.message,
							});
						} else {
							notification.error({
								message: "Error",
								description: "Failed to update user. Please try again.",
							});
						}
					})
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((error) => {
				console.error("Validation failed:", error);
			});
	};

	if (initialLoading) {
		return (
			<div
				style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/customers")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						{customerData ? `Edit User: ${customerData.name}` : "Loading..."}
					</Title>
					<Button
						type="link"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/customers/${customerData?._id}`)}>
						View User
					</Button>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="Edit User"
					extra={
						<div>
							<Button onClick={() => navigate("/customers")} style={{ marginRight: 8 }}>
								Cancel
							</Button>
							<Button type="primary" loading={loading} onClick={handleSubmit}>
								Update User
							</Button>
						</div>
					}>
					<Form form={form} layout="vertical">
						{/* Basic Information */}
						<Row gutter={16}>
							<Col span={8}>
								<Form.Item
									label="Full Name"
									name="name"
									rules={[{ required: true, message: "Please enter user name" }]}>
									<Input placeholder="Enter full name" />
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									label="Email Address"
									name="email"
									rules={[
										{ required: true, message: "Please enter email address" },
										{ type: "email", message: "Please enter a valid email address" },
									]}>
									<Input placeholder="Enter email address" />
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									label="Phone Number"
									name="phone"
									rules={[{ required: true, message: "Please enter phone number" }]}>
									<Input placeholder="Enter phone number" />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={8}>
								<Form.Item
									label="Role"
									name="role"
									rules={[{ required: true, message: "Please select user role" }]}>
									<Select placeholder="Select user role">
										{ROLE_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									label="Password"
									name="password"
									rules={[{ min: 6, message: "Password must be at least 6 characters" }]}>
									<Input.Password placeholder="Enter password" />
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default EditCustomerPage;
